@import "tailwindcss";
@import url('https://fonts.googleapis.com/css2?family=Inter:wght@300;400;500;600;700;800&display=swap');

@theme {
  --color-primary-50: #f0f9ff;
  --color-primary-100: #e0f2fe;
  --color-primary-200: #bae6fd;
  --color-primary-300: #7dd3fc;
  --color-primary-400: #38bdf8;
  --color-primary-500: #0ea5e9;
  --color-primary-600: #0284c7;
  --color-primary-700: #0369a1;
  --color-primary-800: #075985;
  --color-primary-900: #0c4a6e;

  --color-dark-50: #f8fafc;
  --color-dark-100: #f1f5f9;
  --color-dark-200: #e2e8f0;
  --color-dark-300: #cbd5e1;
  --color-dark-400: #94a3b8;
  --color-dark-500: #64748b;
  --color-dark-600: #475569;
  --color-dark-700: #334155;
  --color-dark-800: #1e293b;
  --color-dark-900: #0f172a;
  --color-dark-950: #020617;

  --font-family-sans: 'Inter', system-ui, sans-serif;
  --font-family-display: 'Cal Sans', 'Inter', system-ui, sans-serif;

  --animate-fade-in: fadeIn 0.3s ease-out;
  --animate-slide-up: slideUp 0.3s ease-out;
  --animate-slide-down: slideDown 0.3s ease-out;
  --animate-scale-in: scaleIn 0.2s ease-out;
  --animate-pulse-slow: pulse 3s cubic-bezier(0.4, 0, 0.6, 1) infinite;
  /* Used by the leads drawer and the lead detail panel. These were referenced
     in the markup but never declared, so both panels appeared with no motion. */
  --animate-slide-in-left: slideInLeft 0.25s ease-out;
  --animate-slide-in-right: slideInRight 0.25s ease-out;

  @keyframes fadeIn {
    0% { opacity: 0; }
    100% { opacity: 1; }
  }
  @keyframes slideInLeft {
    0% { transform: translateX(-100%); }
    100% { transform: translateX(0); }
  }
  @keyframes slideInRight {
    0% { transform: translateX(100%); }
    100% { transform: translateX(0); }
  }
  @keyframes slideUp {
    0% { transform: translateY(10px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes slideDown {
    0% { transform: translateY(-10px); opacity: 0; }
    100% { transform: translateY(0); opacity: 1; }
  }
  @keyframes scaleIn {
    0% { transform: scale(0.95); opacity: 0; }
    100% { transform: scale(1); opacity: 1; }
  }
}

* {
  box-sizing: border-box;
}

html {
  scroll-behavior: smooth;
}

body {
  margin: 0;
  font-family: var(--font-family-sans);
  -webkit-font-smoothing: antialiased;
  -moz-osx-font-smoothing: grayscale;
}

#root {
  width: 100%;
  min-height: 100vh;
}

/* Mapbox GL overrides */
.maplibregl-map {
  font-family: var(--font-family-sans) !important;
}

/* ---------- Map markers ---------- */

/* Endpoint pins (route origin / destination) */
.leadmap3d-marker {
  position: relative; /* required: children use position:absolute; inset:0 */
  display: grid;
  place-items: center;
  cursor: pointer;
}

.leadmap3d-endpoint {
  display: block;
  width: 14px;
  height: 14px;
  border-radius: 9999px;
  border: 3px solid #ffffff;
  box-shadow: 0 1px 4px rgba(15, 23, 42, 0.45);
}

/* Device location dot */
.leadmap3d-marker--device {
  cursor: default;
  pointer-events: auto;
}

.leadmap3d-device-core {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: #0ea5e9;
  border: 3px solid #ffffff;
  box-shadow:
    0 0 0 1px rgba(2, 132, 199, 0.35),
    0 2px 6px rgba(15, 23, 42, 0.5);
}

.leadmap3d-device-halo {
  position: absolute;
  inset: 0;
  border-radius: 9999px;
  background: rgba(14, 165, 233, 0.35);
  animation: leadmap3d-pulse 2s ease-out infinite;
}

@keyframes leadmap3d-pulse {
  0% {
    transform: scale(1);
    opacity: 0.55;
  }
  70% {
    transform: scale(3.2);
    opacity: 0;
  }
  100% {
    transform: scale(3.2);
    opacity: 0;
  }
}

@media (prefers-reduced-motion: reduce) {
  .leadmap3d-device-halo {
    animation: none;
    opacity: 0.25;
  }
}

/* ---------- MapLibre popup — our place-info card supplies its own chrome ---------- */
.maplibregl-popup-content {
  padding: 0 !important;
  border-radius: 16px !important;
  box-shadow: 0 20px 25px -5px rgba(0, 0, 0, 0.15), 0 8px 10px -6px rgba(0, 0, 0, 0.1) !important;
}

.maplibregl-popup-close-button {
  padding: 8px 12px !important;
  font-size: 18px !important;
  color: #64748b !important;
}

.maplibregl-popup-close-button:hover {
  color: #0f172a !important;
}

/* Place info popup — strip MapLibre chrome so our card styles apply */
.leadmap3d-place-popup .maplibregl-popup-content {
  padding: 0 !important;
  border-radius: 16px !important;
  background: transparent !important;
  box-shadow: none !important;
  border: none !important;
  overflow: visible !important;
}

.leadmap3d-place-popup .maplibregl-popup-content > div {
  display: contents !important;
}

.leadmap3d-place-popup .maplibregl-popup-tip {
  border-top-color: #ffffff !important;
  border-bottom-color: #ffffff !important;
  z-index: -1;
}

/* Custom scrollbar */
::-webkit-scrollbar {
  width: 8px;
  height: 8px;
}

::-webkit-scrollbar-track {
  background: #f1f5f9;
}

::-webkit-scrollbar-thumb {
  background: #cbd5e1;
  border-radius: 4px;
}

::-webkit-scrollbar-thumb:hover {
  background: #94a3b8;
}

/* Three.js canvas */
.three-canvas {
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: 100%;
  pointer-events: none;
}

/* Animation utilities */
@keyframes shimmer {
  0% { background-position: -200% 0; }
  100% { background-position: 200% 0; }
}

.animate-shimmer {
  background: linear-gradient(90deg, #f1f5f9 25%, #e2e8f0 50%, #f1f5f9 75%);
  background-size: 200% 100%;
  animation: shimmer 1.5s infinite;
}

/* Focus visible */
*:focus-visible {
  outline: 2px solid #0ea5e9;
  outline-offset: 2px;
}

/* Map controls */
.map-control-button {
  @apply flex items-center justify-center w-10 h-10 rounded-lg bg-white/90 backdrop-blur-sm shadow-sm border border-slate-200 text-slate-600 hover:bg-slate-50 hover:text-slate-900 transition-all duration-200 active:scale-95;
}

.map-control-button.active {
  @apply bg-primary-500 text-white border-primary-500 hover:bg-primary-600;
}

/* Lead cards */
.lead-card {
  @apply bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50 shadow-sm hover:shadow-lg transition-all duration-300 overflow-hidden;
}

.lead-card:hover {
  transform: translateY(-2px);
}

/* Dashboard sidebar */
.sidebar-link {
  @apply flex items-center gap-3 px-4 py-3 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors font-medium;
}

.sidebar-link.active {
  @apply bg-primary-50 text-primary-700 font-semibold;
}

/* Stat cards */
.stat-card {
  @apply bg-white/80 backdrop-blur-sm rounded-xl border border-slate-200/50 p-6 shadow-sm hover:shadow-md transition-shadow;
}

/* Button variants */
.btn-primary {
  @apply inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-primary-600 text-white font-medium hover:bg-primary-700 active:scale-[0.98] transition-all duration-200 shadow-sm shadow-primary-500/20;
}

.btn-secondary {
  @apply inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-white text-slate-700 font-medium border border-slate-300 hover:bg-slate-50 active:scale-[0.98] transition-all duration-200;
}

.btn-ghost {
  @apply inline-flex items-center justify-center gap-2 px-4 py-2 rounded-lg text-slate-600 hover:bg-slate-100 hover:text-slate-900 transition-colors font-medium;
}

.btn-danger {
  @apply inline-flex items-center justify-center gap-2 px-6 py-3 rounded-lg bg-red-600 text-white font-medium hover:bg-red-700 active:scale-[0.98] transition-all duration-200;
}

/* Input styles */
.input-field {
  @apply w-full px-4 py-3 rounded-lg border border-slate-300 bg-white text-slate-900 placeholder-slate-400 focus:border-primary-500 focus:ring-2 focus:ring-primary-500/20 focus:outline-none transition-all duration-200;
}

/* Badge styles */
.badge {
  @apply inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium;
}

.badge-success {
  @apply bg-green-100 text-green-800;
}

.badge-warning {
  @apply bg-amber-100 text-amber-800;
}

.badge-danger {
  @apply bg-red-100 text-red-800;
}

.badge-info {
  @apply bg-blue-100 text-blue-800;
}

.badge-primary {
  @apply bg-primary-100 text-primary-800;
}

/* Loading skeleton */
.skeleton {
  @apply animate-pulse bg-slate-200 rounded;
}

/* Glass morphism */
.glass {
  @apply bg-white/70 backdrop-blur-xl border border-white/20 shadow-xl;
}

.glass-dark {
  @apply bg-slate-900/70 backdrop-blur-xl border border-slate-700/50 shadow-xl;
}